import CONFIG from "config/config";
import { callAuthorizedGraphQLEndpoint } from "module/graphQL";
import { useState, useEffect, useRef, useCallback } from "react";
import { retryWithBackoff } from "../../../utils/retryWithBackoff";
import { debounce } from "lodash";

export enum DesignSource {
  UPLOAD = "upload",
  FLYER_BUILDER = "flyer_builder",
  FIGMA = "figma",
}

export const DESIGN_STATUS = {
  ACTIVE: 0,
  ARCHIVED: -1,
};

export interface GQLDesign {
  id?: string;
  filename: string;
  s3Link?: string;
  s3CmykLink?: string;
  isUploaded?: boolean;
  clientId?: string;
  htmlContent?: string;
  createdAt?: string;
  thumbnailUrl?: string;
  updatedAt?: string;
  format?: string;
  status?: DesignStatus;
  authorId?: string;
  author?: {
    id: string;
    fullName: string;
  };
  addressBox?: boolean;
  hasHtmlContent?: boolean;
  designSource?: DesignSource;
  hasFigmaPlaceholders?: boolean | null;
  usedByCampaign?: boolean | null;
}
export enum DesignStatus {
  ACTIVE = 0,
  ARCHIVED = -1,
}

export interface Design {
  id: string;
  imageSrc: string;
  designName: string;
  format: string;
  designer: string;
  status: DesignStatus;
  htmlContent?: string;
  dateCreated: Date;
  updatedAt: Date;
  thumbnailUrl?: string;
  s3Link?: string;
  s3CmykLink?: string;
  addressBox?: boolean;
  hasHtmlContent?: boolean;
  isUploaded?: boolean;
  designSource?: DesignSource;
  hasFigmaPlaceholders?: boolean | null;
  usedByCampaign?: boolean | null;
}

export interface UpdateDesignInput {
  designId: number;
  design: {
    filename?: string;
    s3Link?: string;
    s3CmykLink?: string;
    isUploaded?: boolean;
    htmlContent?: string;
    thumbnailUrl?: string;
    format?: string;
    status?: number;
    authorId?: string;
    addressBox?: boolean;
    designSource?: DesignSource;
  };
}

export interface DesignFilters {
  search?: string;
  format?: string;
  status?: string;
  clientId?: string;
  sortBy?: "designName" | "dateCreated" | "status" | "format" | "designer";
  sortDirection?: "asc" | "desc";
}

export interface PaginationInput {
  page?: number;
  limit?: number;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface DesignConnection {
  designs: Design[];
  pagination: PaginationInfo;
}

export interface DesignTemplate {
  id: string;
  name: string;
  description?: string;
  roomId?: string;
  thumbnail?: string;
  isPublic: boolean;
  orientation?: string;
  format?: string;
  promoOffer?: string;
  industry?: string;
  useCase?: string;
  uspsCompliant?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SaveTemplateInput {
  name: string;
  roomId: string;
  orientation: string;
  format: string;
  thumbnail?: string;
  description?: string;
  isPublic: boolean;
  promoOffer?: string;
  industry?: string;
  useCase?: string;
  uspsCompliant?: boolean;
}

const getDesignsGQL = async (
  clientId: string,
  filters: DesignFilters = {},
  pagination?: PaginationInput
): Promise<{ designs: Design[]; pagination: PaginationInfo | null }> => {
  if (!clientId) return { designs: [], pagination: null };

  // Default to 300 designs limit if no pagination is provided
  const paginationParams = pagination || { page: 1, limit: 300 };

  const designs = await callAuthorizedGraphQLEndpoint(
    `query getDesigns($clientId: ID, $filters: DesignFilters, $pagination: PaginationInput){
    getDesigns(clientId: $clientId, filters: $filters, pagination: $pagination){
      designs {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        thumbnailUrl
        updatedAt
        createdAt
        format
        status
        authorId
        addressBox
        hasHtmlContent
        designSource
        hasFigmaPlaceholders
        usedByCampaign
        author {
          id
          fullName
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
      }
    }
    }`,
    {
      clientId,
      filters,
      pagination: paginationParams,
    },
    CONFIG.FLYER_API_URL
  );
  const data = await (designs as any).json();

  const designData = data.data.getDesigns;
  const processedDesigns = designData.designs.map((design: GQLDesign) => ({
    id: design.id,
    imageSrc: design.thumbnailUrl,
    designName: design.filename,
    format: design.format,
    designer: (design.author && design.author.fullName) || `ID: ${design.authorId}`,
    status: design.status,
    dateCreated: new Date(Number(design.createdAt)),
    updatedAt: new Date(design.updatedAt || ""),
    thumbnailUrl: design.thumbnailUrl,
    s3Link: design.s3Link,
    s3CmykLink: design.s3CmykLink,
    addressBox: design.addressBox,
    hasHtmlContent: design.hasHtmlContent,
    isUploaded: design.isUploaded,
    designSource: design.designSource,
    hasFigmaPlaceholders: design.hasFigmaPlaceholders,
    usedByCampaign: design.usedByCampaign,
  }));

  return {
    designs: processedDesigns,
    pagination: designData.pagination || null,
  };
};

// Generates and stores the PDF for a design that has none (Figma exports, PAD-32394); returns the signed links.
export const ensureDesignPdfGQL = async (
  designId: string
): Promise<Pick<GQLDesign, "id" | "s3Link" | "s3CmykLink">> => {
  const response = await callAuthorizedGraphQLEndpoint(
    `mutation ensureDesignPdf($designId: ID!) {
      ensureDesignPdf(designId: $designId) {
        id
        s3Link
        s3CmykLink
      }
    }`,
    { designId },
    CONFIG.FLYER_API_URL
  );
  const data = await (response as any).json();
  if (data.errors?.length) {
    throw new Error(
      `ensureDesignPdf failed for design ${designId}: ${data.errors.map((error: any) => error.message).join(", ")}`
    );
  }
  const design = data.data?.ensureDesignPdf;
  if (!design) throw new Error(`ensureDesignPdf returned no design for ${designId}`);
  return design;
};

export type PrepareDesignForEditMode = "COPY" | "ORIGINAL";

export interface PreparedDesignForEdit {
  designId: string;
  fileLink: string;
  filename: string;
}

// Renders a clean PDF of a Figma design for the design tool; COPY also creates the "(Copy)" design (PAD-32396).
export const prepareDesignForEditGQL = async (
  designId: string,
  mode: PrepareDesignForEditMode
): Promise<PreparedDesignForEdit> => {
  const response = await callAuthorizedGraphQLEndpoint(
    `mutation prepareDesignForEdit($designId: ID!, $mode: String!) {
      prepareDesignForEdit(designId: $designId, mode: $mode) {
        designId
        fileLink
        filename
      }
    }`,
    { designId, mode },
    CONFIG.FLYER_API_URL
  );
  const data = await (response as any).json();
  if (data.errors?.length) {
    throw new Error(
      `prepareDesignForEdit ${mode} failed for design ${designId}: ${data.errors
        .map((error: any) => error.message)
        .join(", ")}`
    );
  }
  const prepared = data.data?.prepareDesignForEdit;
  if (!prepared?.designId || !prepared?.fileLink) {
    throw new Error(`prepareDesignForEdit ${mode} returned no file for design ${designId}`);
  }
  return { designId: String(prepared.designId), fileLink: prepared.fileLink, filename: prepared.filename };
};

// The team that owns a design (an admin editing a client's design acts outside their own team).
export const getDesignClientIdGQL = async (designId: string): Promise<string | null> => {
  const response = await callAuthorizedGraphQLEndpoint(
    `query getDesignClient($id: ID!) {
      getDesign(id: $id) {
        id
        clientId
      }
    }`,
    { id: designId },
    CONFIG.FLYER_API_URL
  );
  const data = await (response as any).json();
  if (data.errors?.length) {
    throw new Error(`getDesign failed for design ${designId}: ${data.errors.map((e: any) => e.message).join(", ")}`);
  }
  const clientId = data.data?.getDesign?.clientId;
  return clientId ? String(clientId) : null;
};

// The few fields the design tool entry needs to decide whether a Figma choice is pending.
export const getDesignEditInfoGQL = async (designId: string): Promise<GQLDesign | null> => {
  const response = await callAuthorizedGraphQLEndpoint(
    `query getDesignEditInfo($id: ID!) {
      getDesign(id: $id) {
        id
        filename
        format
        addressBox
        designSource
        hasFigmaPlaceholders
        usedByCampaign
      }
    }`,
    { id: designId },
    CONFIG.FLYER_API_URL
  );
  const data = await (response as any).json();
  if (data.errors?.length) {
    throw new Error(
      `getDesign failed for design ${designId}: ${data.errors.map((error: any) => error.message).join(", ")}`
    );
  }
  return data.data?.getDesign ?? null;
};

const createDesignGQL = async (clientId: string, designPayload: GQLDesign): Promise<Design> => {
  const design = await callAuthorizedGraphQLEndpoint(
    `mutation createDesign($clientId: ID!, $design: DesignInput!) {
      createDesign(clientId: $clientId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        format
        status
        authorId
        updatedAt
        author {
          id
          fullName
        }
      }
    }`,
    {
      clientId,
      design: {
        filename: designPayload.filename,
        s3Link: designPayload.s3Link,
        s3CmykLink: designPayload.s3CmykLink,
        isUploaded: designPayload.isUploaded,
        format: designPayload.format,
        status: DESIGN_STATUS.ACTIVE,
        // DesignInput.authorId is a GraphQL String, but callers pass the numeric id from the session snapshot (PAD-31981)
        authorId: designPayload.authorId != null ? String(designPayload.authorId) : null,
        thumbnailUrl: designPayload.thumbnailUrl,
      },
    },
    CONFIG.FLYER_API_URL
  );

  const data = await (design as any).json();

  return {
    id: data.data.createDesign.id,
    imageSrc: data.data.createDesign.thumbnailUrl,
    designName: data.data.createDesign.filename,
    format: data.data.createDesign.format,
    designer:
      (data.data.createDesign.author && data.data.createDesign.author.fullName) ||
      `ID: ${data.data.createDesign.authorId}`,
    status: data.data.createDesign.status,
    updatedAt: new Date(data.data.createDesign.updatedAt),
    dateCreated: new Date(Number(data.data.createDesign.createdAt)),
    thumbnailUrl: data.data.createDesign.thumbnailUrl,
    s3Link: data.data.createDesign.s3Link,
    s3CmykLink: data.data.createDesign.s3CmykLink,
    isUploaded: data.data.createDesign.isUploaded,
  };
};

const activateDesignGQL = async (campaignId: string, designId: string) => {
  const design = await callAuthorizedGraphQLEndpoint(
    `mutation activateDesign($campaignId: ID!, $designId: ID!) {
      activateDesign(campaignId: $campaignId, designId: $designId) {
        id
        campaignId
        designId
        selectedDesignId
        activeRoomId
      }
    }`,
    {
      campaignId,
      designId,
    },
    CONFIG.FLYER_API_URL
  );
  const data = await (design as any).json();
  return data.data.activateDesign;
};

// Template-related GraphQL functions
const getDesignTemplatesGQL = async (): Promise<DesignTemplate[]> => {
  const templates = await callAuthorizedGraphQLEndpoint(
    `query getDesignTemplates{
      getDesignTemplates{
        id
        name
        description
        roomId
        thumbnail
        isPublic
        orientation
        format
        promoOffer
        industry
        useCase
        uspsCompliant
        createdAt
        updatedAt
      }
    }`,
    {},
    CONFIG.FLYER_API_URL
  );
  const data = await (templates as any).json();
  return data.data.getDesignTemplates || [];
};

const createDesignTemplateGQL = async (template: SaveTemplateInput): Promise<DesignTemplate> => {
  try {
    const response = await callAuthorizedGraphQLEndpoint(
      `mutation createDesignTemplate($template: DesignTemplateInput!) {
        createDesignTemplate(template: $template) {
          id
          name
          description
          roomId
          thumbnail
          isPublic
          orientation
          format
          promoOffer
          industry
          useCase
          uspsCompliant
          createdAt
          updatedAt
        }
      }`,
      {
        template,
      },
      CONFIG.FLYER_API_URL
    );

    const data = await (response as any).json();

    // Check for GraphQL errors
    if (data.errors && data.errors.length > 0) {
      const errorMessage = data.errors.map((error: any) => error.message).join(", ");
      console.error("GraphQL errors:", data.errors);
      throw new Error(`GraphQL error: ${errorMessage}`);
    }

    // Check if the template was actually created
    if (!data.data || !data.data.createDesignTemplate) {
      console.error("No template data returned:", data);
      throw new Error("Template creation failed - no data returned");
    }

    const createdTemplate = data.data.createDesignTemplate;

    // Validate the created template has required fields
    if (!createdTemplate.id) {
      console.error("Template created but no ID returned:", createdTemplate);
      throw new Error("Template creation failed - no ID returned");
    }

    console.log("Template created successfully:", createdTemplate);
    return createdTemplate;
  } catch (error) {
    console.error("Error in createDesignTemplateGQL:", error);

    // Re-throw with more specific error messages
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("Unknown error occurred during template creation");
    }
  }
};

const applyTemplateToDesignGQL = async (templateId: string, designId: string): Promise<Design> => {
  const design = await callAuthorizedGraphQLEndpoint(
    `mutation applyTemplateToDesign($templateId: ID!, $designId: ID!) {
      applyTemplateToDesign(templateId: $templateId, designId: $designId) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
      }
    }`,
    {
      templateId,
      designId,
    },
    CONFIG.FLYER_API_URL
  );
  const data = await (design as any).json();
  return data.data.applyTemplateToDesign;
};

const deleteDesignTemplateGQL = async (templateId: string): Promise<boolean> => {
  const result = await callAuthorizedGraphQLEndpoint(
    `mutation deleteDesignTemplate($templateId: ID!) {
      deleteDesignTemplate(templateId: $templateId)
    }`,
    {
      templateId,
    },
    CONFIG.FLYER_API_URL
  );
  const data = await (result as any).json();
  return data.data.deleteDesignTemplate;
};

const getDesignTemplateGQL = async (id: string): Promise<DesignTemplate> => {
  const template = await callAuthorizedGraphQLEndpoint(
    `query getDesignTemplate($id: ID!){
      getDesignTemplate(id: $id){
        id
        name
        description
        roomId
        thumbnail
        isPublic
        orientation
        format
        promoOffer
        industry
        useCase
        uspsCompliant
        createdAt
        updatedAt
      }
    }`,
    {
      id,
    },
    CONFIG.FLYER_API_URL
  );
  const data = await (template as any).json();
  return data.data.getDesignTemplate;
};

export const updateDesignGQL = (payload: UpdateDesignInput) =>
  callAuthorizedGraphQLEndpoint(
    `mutation updateDesign($designId: ID!, $design: DesignInput!) {
      updateDesign(designId: $designId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        updatedAt
        format
        status
        authorId
        addressBox
        author {
          id
          fullName
        }
      } 
    }`,
    {
      designId: payload.designId,
      design: payload.design,
    },
    CONFIG.FLYER_API_URL
  );

const archiveDesignGQL = async (designId: string): Promise<Design> => {
  const design = await callAuthorizedGraphQLEndpoint(
    `mutation updateDesign($designId: ID!, $design: DesignInput!) {
      updateDesign(designId: $designId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        format
        status
        authorId
        updatedAt
        author {
          id
          fullName
        }
      }
    }`,
    {
      designId,
      design: {
        status: DESIGN_STATUS.ARCHIVED,
      },
    },
    CONFIG.FLYER_API_URL
  );

  const data = await (design as any).json();

  return {
    id: data.data.updateDesign.id,
    imageSrc: data.data.updateDesign.thumbnailUrl,
    designName: data.data.updateDesign.filename,
    format: data.data.updateDesign.format,
    designer:
      (data.data.updateDesign.author && data.data.updateDesign.author.fullName) ||
      `ID: ${data.data.updateDesign.authorId}`,
    status: data.data.updateDesign.status,
    updatedAt: new Date(data.data.updateDesign.updatedAt),
    dateCreated: new Date(Number(data.data.updateDesign.createdAt)),
    thumbnailUrl: data.data.updateDesign.thumbnailUrl,
    s3Link: data.data.updateDesign.s3Link,
    s3CmykLink: data.data.updateDesign.s3CmykLink,
  };
};

const duplicateDesignGQL = async (clientId: string, originalDesignId: string): Promise<Design> => {
  // First get the original design
  const originalDesign = await callAuthorizedGraphQLEndpoint(
    `query getDesign($designId: ID!) {
      getDesign(designId: $designId) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        updatedAt
        format
        status
        authorId
        addressBox
        hasHtmlContent
        htmlContent
        author {
          id
          fullName
        }
      }
    }`,
    {
      designId: originalDesignId,
    },
    CONFIG.FLYER_API_URL
  );

  const originalData = await (originalDesign as any).json();
  const original = originalData.data.getDesign;

  // Create a new design with the same content but different name
  const newDesign = await callAuthorizedGraphQLEndpoint(
    `mutation createDesign($clientId: ID!, $design: DesignInput!) {
      createDesign(clientId: $clientId, design: $design) {
        id
        filename
        s3Link
        s3CmykLink
        isUploaded
        clientId
        createdAt
        thumbnailUrl
        format
        status
        authorId
        updatedAt
        author {
          id
          fullName
        }
      }
    }`,
    {
      clientId,
      design: {
        filename: `${original.filename} (Copy)`,
        s3Link: original.s3Link,
        s3CmykLink: original.s3CmykLink,
        isUploaded: original.isUploaded,
        format: original.format,
        status: DESIGN_STATUS.ACTIVE,
        authorId: original.authorId,
        thumbnailUrl: original.thumbnailUrl,
      },
    },
    CONFIG.FLYER_API_URL
  );

  const data = await (newDesign as any).json();

  return {
    id: data.data.createDesign.id,
    imageSrc: data.data.createDesign.thumbnailUrl,
    designName: data.data.createDesign.filename,
    format: data.data.createDesign.format,
    designer:
      (data.data.createDesign.author && data.data.createDesign.author.fullName) ||
      `ID: ${data.data.createDesign.authorId}`,
    status: data.data.createDesign.status,
    updatedAt: new Date(data.data.createDesign.updatedAt),
    dateCreated: new Date(Number(data.data.createDesign.createdAt)),
    thumbnailUrl: data.data.createDesign.thumbnailUrl,
    s3Link: data.data.createDesign.s3Link,
    s3CmykLink: data.data.createDesign.s3CmykLink,
  };
};

interface UseDesignManagementProps {
  clientId: string;
  filters?: DesignFilters;
  pagination?: PaginationInput;
}

const useDesignManagement = ({ clientId, filters = {}, pagination }: UseDesignManagementProps) => {
  const [designs, setDesigns] = useState<Design[]>([]);
  const [templates, setTemplates] = useState<DesignTemplate[]>([]);
  const [publicTemplates, setPublicTemplates] = useState<DesignTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);

  const fetchDesigns = async (currentFilters = filters, currentPagination = pagination) => {
    setLoading(true);
    try {
      const [designsResult, templatesResult] = await Promise.all([
        getDesignsGQL(clientId, currentFilters, currentPagination),
        getDesignTemplatesGQL(),
      ]);

      setDesigns(designsResult.designs);
      setPaginationInfo(designsResult.pagination);
      setTemplates(templatesResult);
      setPublicTemplates(templatesResult.filter((template) => template.isPublic));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching designs:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesigns();
  }, [clientId, JSON.stringify(filters), JSON.stringify(pagination)]);

  // Debounced refetch function for visibility change
  const debouncedRefetch = useCallback(
    debounce(() => {
      if (clientId) {
        fetchDesigns();
      }
    }, 500),
    [clientId]
  );

  // Refetch designs when user returns to the tab (e.g., after editing a design in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        debouncedRefetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      debouncedRefetch.cancel();
    };
  }, [debouncedRefetch]);

  const createDesign = async (design: GQLDesign) => {
    setLoading(true);
    try {
      const res = await retryWithBackoff(
        () => createDesignGQL(clientId, design),
        3, // Max 3 retries for design creation
        1000
      );
      setDesigns((prev) => [...prev, res]);
      setLoading(false);
      return res;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const activateDesign = async (campaignId: string, designId: string) => {
    setLoading(true);
    try {
      const res = await retryWithBackoff(
        () => activateDesignGQL(campaignId, designId),
        3, // Max 3 retries for activation
        1000
      );
      setLoading(false);
      return res;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const createDesignTemplate = async (input: SaveTemplateInput) => {
    setLoading(true);
    try {
      // Use retry mechanism for template creation
      const res = await retryWithBackoff(
        () => createDesignTemplateGQL(input),
        2, // Max 2 retries for creation operations
        1500 // 1.5 second base delay
      );

      // Add the new template to the local state
      setTemplates((prev) => [res, ...prev]);

      // Also update public templates if the template is public
      if (res.isPublic) {
        setPublicTemplates((prev) => [res, ...prev]);
      }

      // Optionally refresh the template list to ensure consistency
      // This helps verify the template was actually saved on the server
      setTimeout(async () => {
        try {
          const templates = await retryWithBackoff(() => getDesignTemplatesGQL(), 1, 1000);
          setTemplates(templates);
          setPublicTemplates(templates.filter((template: DesignTemplate) => template.isPublic));
        } catch (error) {
          console.warn("Failed to refresh template list:", error);
        }
      }, 1000);

      setLoading(false);
      return res;
    } catch (error) {
      setLoading(false);
      throw error; // Re-throw to let the caller handle the error
    }
  };

  const applyTemplateToDesign = async (templateId: string, designId: string) => {
    setLoading(true);
    const res = await applyTemplateToDesignGQL(templateId, designId);
    // Update the design in the designs list
    setDesigns((prev) =>
      prev.map((design) =>
        design.id === designId
          ? {
              ...design,
              htmlContent: res.htmlContent,
            }
          : design
      )
    );
    setLoading(false);
    return res;
  };

  const deleteTemplate = async (templateId: string) => {
    setLoading(true);
    const res = await deleteDesignTemplateGQL(templateId);
    if (res) {
      setTemplates((prev) => prev.filter((template) => template.id !== templateId));
    }
    setLoading(false);
    return res;
  };

  const updateDesign = async (payload: UpdateDesignInput, skipUpdateDesigns = true) => {
    const newDesign = {
      filename: payload.design.filename,
      s3Link: payload.design.s3Link,
      s3CmykLink: payload.design.s3CmykLink,
      isUploaded: payload.design.isUploaded,
      thumbnailUrl: payload.design.thumbnailUrl,
      format: payload.design.format,
      status: payload.design.status,
      htmlContent: payload.design.htmlContent,
      addressBox: payload.design.addressBox,
      designSource: payload.design.designSource,
    };

    const res = await updateDesignGQL({
      ...payload,
      design: newDesign,
    });
    if (!skipUpdateDesigns) {
      setDesigns((prev) =>
        prev.map((design) => (design.id === payload.designId.toString() ? { ...design, ...res } : design))
      );
    }
    return res;
  };

  const duplicateDesign = (id: string) => {
    console.log("duplicateDesign", id);
  };
  const archiveDesign = (id: string) => {
    console.log("archiveDesign", id);
  };

  const getDesign = async (designId: string): Promise<GQLDesign> => {
    setLoading(true);
    const design = await callAuthorizedGraphQLEndpoint(
      `query getDesign($id: ID!) {
        getDesign(id: $id) {
          id
          filename
          s3Link
          s3CmykLink
          isUploaded
          clientId
          createdAt
          thumbnailUrl
          updatedAt
          format
          status
          authorId
          addressBox
          hasHtmlContent
          htmlContent
          designSource
          hasFigmaPlaceholders
          usedByCampaign
          author {
            id
            fullName
          }
        }
      }`,
      {
        id: designId,
      },
      CONFIG.FLYER_API_URL
    );
    const data = await (design as any).json();
    setLoading(false);
    return data.data.getDesign;
  };

  const refreshDesign = async (designId: string): Promise<GQLDesign> => {
    // Fetch the latest design data including updated thumbnail
    const updatedDesign = await callAuthorizedGraphQLEndpoint(
      `query getDesign($id: ID!) {
        getDesign(id: $id) {
          id
          filename
          s3Link
          s3CmykLink
          isUploaded
          clientId
          createdAt
          thumbnailUrl
          updatedAt
          format
          status
          authorId
          htmlContent
          addressBox
          hasHtmlContent
          author {
            id
            fullName
          }
        }
      }`,
      {
        id: designId,
      },
      CONFIG.FLYER_API_URL
    );
    const data = await (updatedDesign as any).json();
    const design = data.data.getDesign;

    // Update the design in the local state with refreshed data
    setDesigns((prev) =>
      prev.map((d) => {
        if (d.id === designId) {
          return {
            ...d,
            imageSrc: design.thumbnailUrl,
            designName: design.filename,
            format: design.format,
            dateCreated: design.createdAt ? new Date(Number(design.createdAt)) : d.dateCreated,
            status: design.status,
            updatedAt: new Date(design.updatedAt || ""),
            s3Link: design.s3Link,
            s3CmykLink: design.s3CmykLink,
          };
        }
        return d;
      })
    );

    return design;
  };

  return {
    designs,
    setDesigns,
    templates,
    setTemplates,
    publicTemplates,
    setPublicTemplates,
    createDesign,
    updateDesign,
    duplicateDesign,
    activateDesign,
    archiveDesign,
    createDesignTemplate,
    applyTemplateToDesign,
    deleteTemplate,
    getDesignTemplateGQL,
    getDesign,
    refreshDesign,
    loading,
    paginationInfo,
    fetchDesigns,
  };
};

export default useDesignManagement;
